// Implements: F-UIX-017
/**
 * The keys of the editor's focus mode (UX-05, the mockup's Shift+\): Shift+\ toggles it and Escape
 * leaves it.
 *
 * Shift+\ is read by the physical key (`Backslash`, or `IntlBackslash` on ISO keyboards) or by the
 * character a layout reports (`\` or `|`), never from a field, never with Ctrl, Cmd or Alt. Escape
 * leaves focus mode only while nothing is open over the page (a dialog, a popover, a menu): that
 * Escape is theirs. The listener runs in the capture phase and claims the Escape it uses, so the
 * page's own Escape (cancelling a turn in flight, F-UIX-017) never fires from the same press: a paid
 * turn is never cancelled by somebody who only meant to leave focus mode.
 */

import { useEffect, useRef } from "react";

import { isEditableTarget } from "./editableTarget";

const OVERLAY = '[role="dialog"], [aria-modal="true"], [role="menu"], dialog[open]';

function isFocusKey(event: KeyboardEvent): boolean {
  if (!event.shiftKey || event.ctrlKey || event.metaKey || event.altKey) return false;
  return event.code === "Backslash" || event.code === "IntlBackslash" || event.key === "\\" || event.key === "|";
}

export function useFocusModeKeys(focus: boolean, onToggle: () => void): void {
  const toggle = useRef(onToggle);
  useEffect(() => {
    toggle.current = onToggle;
  }, [onToggle]);

  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent): void => {
      if (event.defaultPrevented || event.repeat) return;
      if (event.key === "Escape") {
        if (!focus || document.querySelector(OVERLAY) !== null) return;
        event.preventDefault();
        toggle.current();
        return;
      }
      if (!isFocusKey(event) || isEditableTarget(event.target)) return;
      if (document.querySelector(OVERLAY) !== null) return;
      event.preventDefault();
      toggle.current();
    };
    window.addEventListener("keydown", onKeyDown, true);
    return () => {
      window.removeEventListener("keydown", onKeyDown, true);
    };
  }, [focus]);
}
